import React, { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { OptionInternal, SourceConfig } from '../../shared/options';
import { OptionStyle, validOptionTypes } from '../models';
import { useTranslation } from '../translations';
import { mapObjectToArray, useInputCheckboxModel, useInputModel } from '../utils';
import { CheckboxLabel } from './CheckboxLabel';
import { OptionEditorMinMax, OptionEditorSelect } from './optionEditorComponents';
import { InlineHelp } from '.';

interface OptionEditorProps {
  readonly option: OptionInternal;
  readonly setOption: Dispatch<SetStateAction<OptionInternal>>;
  readonly setIsEditing: (state: boolean) => void;
}

export const OptionEditor: FC<OptionEditorProps> = ({
  option,
  setOption,
  setIsEditing,
}: OptionEditorProps) => {
  const { ts } = useTranslation();
  const setType = useInputModel(setOption, 'type');
  const setName = useInputModel(setOption, 'name');
  const setDescription = useInputModel(setOption, 'description');
  const setDisplayOnCashier = useInputCheckboxModel(setOption, 'displayOnCashier');
  const config = SourceConfig[option.source];

  return (
    <Row>
      <Col lg={6} xs={12}>
        <FormGroup>
          <Label for="name">{ts('name')}</Label>
          <Input
            id="name"
            onChange={setName}
            placeholder={ts('name')}
            required
            value={option.name}
          />
        </FormGroup>
      </Col>
      <Col lg={6} xs={12}>
        <FormGroup>
          <Label for="type">
            {ts('type')}
            <InlineHelp>
              <Label>{ts('option_type_information')}</Label>
              <ul className="flushLeft">
                {mapObjectToArray(validOptionTypes, ({ name, description, currency }, key) => {
                  if (!config.allowPrice && currency) {
                    return null;
                  }

                  return (
                    <li key={key}>
                      <strong>{ts(name)}</strong>: {ts(description)}
                    </li>
                  );
                })}
              </ul>
            </InlineHelp>
          </Label>
          <Input
            // Disables the option to change the type of the option if editing
            disabled={option.id !== 0}
            id="type"
            onChange={setType}
            type="select"
            value={option.type}
          >
            {mapObjectToArray(validOptionTypes, ({ name, currency, hidden }, key) => {
              if (!config.allowPrice && currency) {
                return null;
              }

              if (hidden) {
                return null;
              }

              return (
                <option key={key} value={key}>
                  {ts(name)}
                </option>
              );
            })}
          </Input>
        </FormGroup>
      </Col>
      <SelectStyle
        option={option}
        setOption={setOption}
        styles={validOptionTypes[option.type].styles}
      />
      <Col>
        <FormGroup>
          <Label for="description">
            {ts('description')} {ts('optional')}
          </Label>
          <Input
            id="description"
            maxLength={255}
            onChange={setDescription}
            value={option.description}
          />
        </FormGroup>
      </Col>
      {option.source === 'product' && (
        <Col lg={6} xs={12}>
          <FormGroup>
            <CheckboxLabel
              checked={option.displayOnCashier}
              id="displayOnCashier"
              onChange={setDisplayOnCashier}
            >
              {ts('display_on_cashier_interface')}
            </CheckboxLabel>
          </FormGroup>
        </Col>
      )}
      <Col xs={12} />
      {option.type !== 'phone' &&
        option.type !== 'address' &&
        (option.type === 'select' || option.type === 'multi' ? (
          <OptionEditorSelect
            isMulti={option.type === 'multi'}
            option={option}
            setIsEditing={setIsEditing}
            setOption={setOption}
          />
        ) : (
          <OptionEditorMinMax
            label={option.type === 'text' ? 'Length' : 'Value'}
            option={option}
            setOption={setOption}
          />
        ))}
      <Col xs={12}>
        <hr />
      </Col>
    </Row>
  );
};

interface SelectStyleProps {
  readonly styles?: OptionStyle[];
  readonly option: OptionInternal;
  readonly setOption: Dispatch<SetStateAction<OptionInternal>>;
}

const SelectStyle: FC<SelectStyleProps> = ({ styles, option, setOption }) => {
  const setStyle = useInputModel(setOption, 'style');
  const { ts } = useTranslation();

  useEffect(() => {
    if (styles && styles.length > 0 && !option.style) {
      setOption((prev) => ({ ...prev, style: styles[0]!.id }));
    }
  }, [option.style, setOption, styles]);

  if (!styles || styles.length === 0) {
    return null;
  }

  return (
    <>
      <Col lg={6} xs={12} />
      <Col lg={6} xs={12}>
        <Label for="type">{ts('style')}</Label>
        <Input
          id="style"
          onChange={setStyle}
          type="select"
          value={option.style ?? styles[0]?.id ?? ''}
        >
          {styles.map(({ id, name }) => {
            return (
              <option key={id} value={id}>
                {name}
              </option>
            );
          })}
        </Input>
      </Col>
    </>
  );
};
